/**
 * Secure Withdrawal Hook - All withdrawal operations via backend Edge Functions
 * 
 * This hook handles:
 * - Withdrawal cart state management
 * - Server-side stock validation
 * - Secure withdrawal processing
 */

import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Product } from '@/context/SupabaseInventoryContext';
import { useNotifications } from '@/hooks/useNotifications';

export interface SecureWithdrawalItem {
  product: Product;
  quantity: number;
}

export const useSecureWithdrawal = (companyId: string | undefined) => {
  const [cart, setCart] = useState<SecureWithdrawalItem[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const { success, error } = useNotifications();

  /**
   * Add product to withdrawal cart with server-side validation
   */
  const addToCart = useCallback(async (product: Product, quantity: number = 1) => {
    if (!companyId) {
      error({
        title: "Erreur",
        description: "Aucune entreprise sélectionnée"
      });
      return false;
    }

    try {
      // Validate with backend
      const { data, error: apiError } = await supabase.functions.invoke('secure-withdrawal-operations', {
        body: {
          action: 'validate',
          companyId,
          productId: product.id,
          quantity
        }
      });

      if (apiError) throw apiError;

      if (!data.success || !data.valid) {
        error({
          title: "Validation échouée",
          description: data.error || "Stock insuffisant"
        });
        return false;
      }

      // Update local cart state
      setCart(prevCart => {
        const existingItem = prevCart.find(item => item.product.id === product.id);
        if (existingItem) {
          return prevCart.map(item =>
            item.product.id === product.id
              ? { ...item, quantity: item.quantity + quantity }
              : item
          );
        } else {
          return [...prevCart, {
            product,
            quantity
          }];
        }
      });

      success({
        title: "✅ Produit ajouté",
        description: `${product.name} ajouté au retrait`
      });

      return true;
    } catch (err) {
      console.error('Error adding to withdrawal cart:', err);
      error({
        title: "Erreur",
        description: err instanceof Error ? err.message : "Erreur lors de l'ajout"
      });
      return false;
    }
  }, [companyId, success, error]);

  /**
   * Update cart item quantity with server-side validation
   */
  const updateQuantity = useCallback(async (productId: string, newQuantity: number) => {
    if (!companyId) return false;

    if (newQuantity <= 0) {
      return removeFromCart(productId);
    }

    const item = cart.find(i => i.product.id === productId);
    if (!item) return false;

    try {
      const { data, error: apiError } = await supabase.functions.invoke('secure-withdrawal-operations', {
        body: {
          action: 'validate',
          companyId,
          productId,
          quantity: newQuantity
        }
      });

      if (apiError) throw apiError;

      if (!data.success || !data.valid) {
        error({
          title: "Erreur",
          description: data.error || "Stock insuffisant"
        });
        return false;
      }

      // Update local state
      setCart(prevCart =>
        prevCart.map(cartItem =>
          cartItem.product.id === productId
            ? { ...cartItem, quantity: newQuantity }
            : cartItem
        )
      );

      return true;
    } catch (err) {
      console.error('Error updating quantity:', err);
      error({
        title: "Erreur",
        description: "Impossible de mettre à jour la quantité"
      });
      return false;
    }
  }, [companyId, cart, error]);

  /**
   * Remove item from cart
   */
  const removeFromCart = useCallback(async (productId: string) => {
    setCart(prevCart => prevCart.filter(item => item.product.id !== productId));
    return true;
  }, []);

  /**
   * Clear entire cart
   */
  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  /**
   * Process withdrawal via backend with secure validation
   */
  const processWithdrawal = useCallback(async (reason?: string) => {
    if (!companyId) {
      error({
        title: "Erreur",
        description: "Aucune entreprise sélectionnée"
      });
      return false;
    }

    if (cart.length === 0) {
      error({
        title: "Panier vide",
        description: "Ajoutez des produits avant de valider"
      });
      return false;
    }

    setIsProcessing(true);
    try {
      const items = cart.map(item => ({
        productId: item.product.id,
        quantity: item.quantity,
        productName: item.product.name,
        reason
      }));

      const { data, error: apiError } = await supabase.functions.invoke('secure-withdrawal-operations', {
        body: {
          action: 'process',
          companyId,
          items,
          reason: reason || 'Retrait stock'
        }
      });

      if (apiError) throw apiError;

      if (!data.success) {
        error({
          title: "Erreur de retrait",
          description: data.error || "Impossible de traiter le retrait"
        });
        return false;
      }

      success({
        title: "Retrait confirmé !",
        description: `${data.processed} article${data.processed > 1 ? 's' : ''} retiré${data.processed > 1 ? 's' : ''} du stock`
      });

      clearCart();
      return true;
    } catch (err) {
      console.error('Error processing withdrawal:', err);
      error({
        title: "Erreur lors du retrait",
        description: err instanceof Error ? err.message : "Impossible de traiter le retrait"
      });
      return false;
    } finally {
      setIsProcessing(false);
    }
  }, [companyId, cart, success, error, clearCart]);

  return {
    cart,
    isProcessing,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    processWithdrawal
  };
};
